import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, RefreshCw, Handshake, BadgeCheck, MapPin, Phone as PhoneIcon,
  Truck, IndianRupee, ChevronRight,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const TYPE_LABEL = {
  government: 'Government (108)',
  private: 'Private',
  hospital_owned: 'Hospital-owned',
  individual: 'Individual Driver',
};
const TYPE_BADGE = {
  government: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  private: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  hospital_owned: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  individual: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
};

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });


export default function AmbulanceProvidersList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch('engage/ambulance-providers/');
        if (!cancelled) setProviders(resp?.results || resp?.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => providers.filter(p => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return [p.name, p.mobile, p.vehicle_number, p.city].filter(Boolean).join(' ').toLowerCase().includes(q);
  }), [providers, search]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white shadow-sm">
            <Handshake className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Ambulance Partners</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} providers`}
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/engage/ambulance-providers/new')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-orange-600 text-white text-sm shadow-sm hover:bg-orange-700">
          <Plus className="w-3.5 h-3.5" /> Add Provider
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search name, mobile, vehicle number…"
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500 text-gray-900 dark:text-white" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {loading ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        : filtered.length === 0 ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">No providers yet.</div>
        : filtered.map(p => (
          <button key={p.id} onClick={() => navigate(`/engage/ambulance-providers/${p.id}`)}
            className="group text-left bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-orange-300 dark:hover:border-orange-700 transition-all">
            <div className="flex items-start gap-2.5 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white shadow-sm">
                <Truck className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{p.name}</div>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', TYPE_BADGE[p.provider_type] || 'bg-gray-100 text-gray-600')}>
                    {TYPE_LABEL[p.provider_type] || p.provider_type}
                  </span>
                  {p.is_verified && <BadgeCheck className="w-3.5 h-3.5 text-green-500" />}
                </div>
              </div>
            </div>
            <div className="space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
              {p.vehicle_number && <div className="flex items-center gap-1.5"><Truck className="w-3 h-3" /> {p.vehicle_number}</div>}
              {p.mobile && <div className="flex items-center gap-1.5"><PhoneIcon className="w-3 h-3" /> {p.mobile}</div>}
              {p.city && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {p.city}</div>}
            </div>
            <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <span className="text-[11px] inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                <IndianRupee className="w-3 h-3" /> {formatINR(p.default_incentive_amount)}/referral
              </span>
              <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-orange-500 transition-colors" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
